class BmpFreeShippingBar extends HTMLElement {
  static get observedAttributes() {
    return ['data-current', 'data-currency', 'data-fixed-goal', 'data-base-usd-goal', 'data-policy-goal', 'data-policy-currency'];
  }

  #observer = null;
  #updateFrame = null;

  connectedCallback() {
    this.#observeMorphs();
    this.scheduleUpdate();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#updateFrame) {
      cancelAnimationFrame(this.#updateFrame);
      this.#updateFrame = null;
    }
  }

  attributeChangedCallback() {
    if (this.isConnected) this.scheduleUpdate();
  }

  #observeMorphs() {
    this.#observer?.disconnect();

    this.#observer = new MutationObserver(() => {
      this.scheduleUpdate();
    });

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  scheduleUpdate() {
    if (this.#updateFrame) cancelAnimationFrame(this.#updateFrame);

    this.#updateFrame = requestAnimationFrame(() => {
      this.#updateFrame = requestAnimationFrame(() => {
        this.#updateFrame = null;
        this.updateProgress();
      });
    });
  }

  getGoalCents() {
    const fixedGoal = Number(this.dataset.fixedGoal || 0);
    if (fixedGoal > 0) return fixedGoal;

    const rate = Number(window.Shopify?.currency?.rate);
    const baseUsdGoal = Number(this.dataset.baseUsdGoal || 0);

    if (!(baseUsdGoal > 0) || !Number.isFinite(rate) || rate <= 0) return null;
    return Math.round(baseUsdGoal * rate);
  }

  formatMoney(cents, currencyOverride) {
    const currency = currencyOverride || this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || navigator.language || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  updateProgress() {
    const goal = this.getGoalCents();

    if (!goal || goal <= 0) {
      const policyGoal = Number(this.dataset.policyGoal || 0);
      const policyCurrency = this.dataset.policyCurrency;
      const message = this.querySelector('[data-bmp-fsb-message]');
      const track = this.querySelector('[data-bmp-fsb-track]');
      if (policyGoal > 0 && policyCurrency && message) {
        this.#observer?.disconnect();
        message.textContent = `Free shipping on orders of ${this.formatMoney(policyGoal, policyCurrency)} or more.`;
        if (track) track.hidden = true;
        this.removeAttribute('hidden');
        this.style.removeProperty('display');
        this.#observeMorphs();
      } else {
        this.style.display = 'none';
      }
      return;
    }

    const current = Number(this.dataset.current || 0);
    const remaining = Math.max(goal - current, 0);
    const percent = Math.min(Math.max(Math.round((current * 100) / goal), 0), 100);

    const message = this.querySelector('[data-bmp-fsb-message]');
    const track = this.querySelector('[data-bmp-fsb-track]');

    // Avoid reacting to the DOM changes made by this method itself.
    this.#observer?.disconnect();

    if (message) {
      if (remaining > 0) {
        const amount = this.formatMoney(remaining);
        const template = this.dataset.messageTemplate || 'Spend [amount] more to get FREE shipping!';
        message.textContent = template
          .replace('$[amount]', '[amount]')
          .replace('[amount]', amount);
      } else {
        message.textContent = this.dataset.successMessage || 'Congrats! You get FREE shipping!';
      }
    }

    if (track) {
      track.hidden = false;
      track.setAttribute('aria-valuenow', String(percent));
      track.style.setProperty('--bmp-fsb-progress', `${percent}%`);
    }

    this.removeAttribute('hidden');
    this.style.removeProperty('display');

    this.#observeMorphs();
  }
}

if (!customElements.get('bmp-free-shipping-bar')) {
  customElements.define('bmp-free-shipping-bar', BmpFreeShippingBar);
}
